// src/components/routing/ScrollManager.js
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

/**
 * 🧭 GESTION DU DÉFILEMENT À CHAQUE NAVIGATION (monté une seule fois, dans EnhancedAppRoutes)
 * =========================================================================================
 * - Nouvelle page            → affichée depuis le haut, immédiatement
 * - Lien vers la page actuelle → remontée douce vers le haut
 * - Lien avec ancre (#section) → défilement doux jusqu'à la section, même si elle se charge après
 *                                la page (pages chargées à la demande) ; l'en-tête fixe ne la cache pas
 *                                (scroll-margin-top dans index.css)
 * - Retour / Suivant du navigateur → position exacte retrouvée
 * - Liens internes <a href="/..."> → navigation sans rechargement complet du site
 */

const POSITIONS_KEY = 'scroll-positions';
const WAIT_TIMEOUT_MS = 3000;

const readPositions = () => {
  try {
    return JSON.parse(sessionStorage.getItem(POSITIONS_KEY) || '{}');
  } catch (error) {
    return {};
  }
};

const writePositions = (positions) => {
  try {
    sessionStorage.setItem(POSITIONS_KEY, JSON.stringify(positions));
  } catch (error) {
    // Stockage indisponible : la position ne sera simplement pas retrouvée
  }
};

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Défilement vertical ; smooth = false : saut immédiat (même si le CSS impose scroll-behavior: smooth) */
const scrollToY = (top, smooth) => {
  const root = document.documentElement;
  if (smooth) {
    window.scrollTo({ top, left: 0, behavior: 'smooth' });
    return;
  }
  const previous = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  window.scrollTo(0, top);
  root.style.scrollBehavior = previous;
};

/** Répète check() à chaque image jusqu'à ce qu'il réussisse (ou délai dépassé) ; renvoie une fonction d'annulation */
const retryUntil = (check, onTimeout) => {
  const start = Date.now();
  let frame;
  const tick = () => {
    if (check()) return;
    if (Date.now() - start > WAIT_TIMEOUT_MS) {
      onTimeout?.();
      return;
    }
    frame = requestAnimationFrame(tick);
  };
  tick();
  return () => cancelAnimationFrame(frame);
};

/** Lien interne à confier au routeur (et non au navigateur) ? */
const internalTarget = (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return null;
  }
  const anchor = event.target.closest?.('a[href]');
  if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return null;
  const href = anchor.getAttribute('href');
  if (!href || href === '#' || /^(mailto|tel|sms|javascript):/i.test(href)) return null;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  // Fichiers et API : laissés au navigateur
  if (/\.[a-z0-9]{2,5}$/i.test(url.pathname) || url.pathname.startsWith('/api/')) return null;
  return url;
};

const ScrollManager = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const previousPath = useRef(null);
  const currentKey = useRef(location.key);

  // Le site gère lui-même la position (le navigateur la restaurerait avant l'affichage de la page)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  // Mémorise la position de défilement de chaque page visitée (pour Retour / Suivant)
  useEffect(() => {
    let frame = null;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        const positions = readPositions();
        positions[currentKey.current] = window.scrollY;
        writePositions(positions);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Liens internes classiques (<a href="/...">, <a href="#section">) : navigation par le routeur
  useEffect(() => {
    const onClick = (event) => {
      const url = internalTarget(event);
      if (!url) return;
      event.preventDefault();
      navigate(`${url.pathname}${url.search}${url.hash}`);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);

  // Défilement à chaque navigation
  useEffect(() => {
    currentKey.current = location.key;
    const samePage = previousPath.current === location.pathname;
    previousPath.current = location.pathname;
    const smooth = !prefersReducedMotion();

    // Retour / Suivant : position mémorisée, dès que la page est assez haute pour l'atteindre
    const saved = readPositions()[location.key];
    if (navigationType === 'POP' && typeof saved === 'number') {
      return retryUntil(
        () => {
          const reachable = document.documentElement.scrollHeight - window.innerHeight >= saved;
          if (reachable) scrollToY(saved, false);
          return reachable;
        },
        () => scrollToY(saved, false)
      );
    }

    // Ancre : section visée, dès qu'elle existe dans la page
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      return retryUntil(
        () => {
          const element = document.getElementById(id);
          if (element) element.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
          return Boolean(element);
        },
        () => { if (!samePage) scrollToY(0, false); }
      );
    }

    // Nouvelle page : en haut immédiatement ; même page : remontée douce
    scrollToY(0, samePage && smooth);
    return undefined;
  }, [location.key, location.pathname, location.hash, navigationType]);

  return null;
};

export default ScrollManager;
